export interface GnomeSnapshotOwner {
    userId: string;
    generation: number;
}

export type GnomeSnapshotReadIntent = "existing" | "ensure";
export const gnomeSnapshotOwnerKey = (owner: GnomeSnapshotOwner): string =>
    `${owner.userId}:${owner.generation}`;

/** Only in-flight I/O lives here. The authoritative snapshot stays in Provider Kit. */
export function createGnomeSnapshotReader<T>(
    fetchSnapshot: (
        intent: GnomeSnapshotReadIntent,
        signal: AbortSignal,
    ) => Promise<T | null>,
    backgroundTimeoutMs = 5_000,
) {
    const requests = new Map<
        string,
        { owner: string; controller: AbortController; ready: Promise<T | null> }
    >();
    let activeOwner: string | null | undefined;

    function start(
        owner: GnomeSnapshotOwner,
        intent: GnomeSnapshotReadIntent,
    ): Promise<T | null> {
        const ownerKey = gnomeSnapshotOwnerKey(owner);
        if (activeOwner !== undefined && activeOwner !== ownerKey)
            return Promise.reject(
                new DOMException("Gnome session changed", "AbortError"),
            );
        const key = `${ownerKey}:${intent}`;
        const previous = requests.get(key);
        if (previous) return previous.ready;
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout> | undefined;
        const interrupted = new Promise<never>((_resolve, reject) => {
            controller.signal.addEventListener(
                "abort",
                () =>
                    reject(
                        new DOMException("Gnome read cancelled", "AbortError"),
                    ),
                { once: true },
            );
            if (intent === "existing")
                timer = setTimeout(
                    () => controller.abort(),
                    backgroundTimeoutMs,
                );
        });
        const request = {
            owner: ownerKey,
            controller,
            ready: Promise.race([
                Promise.resolve().then(() =>
                    fetchSnapshot(intent, controller.signal),
                ),
                interrupted,
            ]).finally(() => {
                clearTimeout(timer);
                if (requests.get(key) === request) requests.delete(key);
            }),
        };
        requests.set(key, request);
        return request.ready;
    }

    return {
        isCurrentOwner(owner: GnomeSnapshotOwner): boolean {
            return activeOwner === gnomeSnapshotOwnerKey(owner);
        },
        cancelOtherOwners(owner: GnomeSnapshotOwner | null): void {
            const keep = owner ? gnomeSnapshotOwnerKey(owner) : null;
            activeOwner = keep;
            for (const request of requests.values())
                if (request.owner !== keep) request.controller.abort("session");
        },
        async read(
            owner: GnomeSnapshotOwner,
            intent: GnomeSnapshotReadIntent,
            fresh = false,
        ): Promise<T | null> {
            const key = gnomeSnapshotOwnerKey(owner);
            const ensure = requests.get(`${key}:ensure`);
            const existing = requests.get(`${key}:existing`);
            if (fresh) {
                await (ensure ?? existing)?.ready.catch(() => undefined);
                return start(owner, intent);
            }
            if (ensure) return ensure.ready;
            if (intent === "existing") return start(owner, "existing");
            if (existing) {
                const snapshot = await existing.ready.catch(() => null);
                if (snapshot !== null) return snapshot;
                // Do not initialize under another account after an aborted old read.
                if (
                    existing.controller.signal.aborted &&
                    existing.controller.signal.reason === "session"
                )
                    return null;
            }
            return start(owner, "ensure");
        },
    };
}
